import { useNavigate } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useI18n } from '@/hooks/useI18n';
import { useMoneyCopy } from '@/lib/i18n/money-copy';
import { formatPlanAmount, formatPlanDate } from '@/lib/builder/utils/payment-format';
import {
    IPaidInstallment,
    PeopleDictionary,
    personOf,
} from '@/lib/payments/models/payments-dashboard.model';
import { paymentMethodKey } from '@/lib/payments/utils/payment-method';
import { PlatformFeeDueBadge } from '@/lib/payments/components/PlatformFeeDueBadge';

interface RecentPaymentsCardProps {
    rows: IPaidInstallment[];
    /** Anagrafiche citate dalle righe: le righe portano solo la chiave. */
    people: PeopleDictionary;
    isLoading: boolean;
}

/**
 * Ultime rate incassate.
 *
 * Le righe arrivano da `itinerary_installments` e non da `payments`: quella tabella e'
 * leggibile solo da service_role, e comunque contiene un record per tentativo, quindi
 * mostrerebbe anche i pagamenti falliti come se fossero incassi.
 */
export const RecentPaymentsCard = ({ rows, people, isLoading }: RecentPaymentsCardProps) => {
    const { t } = useI18n();
    const money = useMoneyCopy();
    const navigate = useNavigate();

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="p-4 pb-3 flex-shrink-0">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-gray-500" />
                    {t(`${money}.recentTitle`)}
                </CardTitle>
            </CardHeader>

            <CardContent className="p-4 pt-0 flex-1 min-h-0 flex flex-col">
                {rows.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        {isLoading
                            ? t('common.loading')
                            : t(`${money}.recentEmpty`)}
                    </p>
                ) : (
                    /**
                     * La lista scrolla dentro la card: la RPC ne restituisce dieci, ma
                     * l'altezza e' tarata su cinque righe per non far crescere la card
                     * oltre il carosello dei viaggi che le sta accanto.
                     */
                    <ul className="flex-1 min-h-0 max-h-[19rem] xl:max-h-none overflow-y-auto divide-y divide-gray-100 pr-1">
                        {rows.map((row) => (
                            <li
                                key={row.installment_id}
                                className="py-2.5 flex items-start justify-between gap-3"
                            >
                                <div className="min-w-0">
                                    <button
                                        type="button"
                                        className="font-medium text-gray-900 hover:underline text-left truncate block max-w-full"
                                        onClick={() =>
                                            navigate(
                                                `/itinerary-builder?itineraryId=${row.itinerary_id}`
                                            )
                                        }
                                    >
                                        {row.itinerary_title}
                                    </button>
                                    <span className="block text-xs text-gray-500 truncate">
                                        {[
                                            personOf(people, row.client_key)?.name ?? null,
                                            row.label,
                                            (() => {
                                                const key = paymentMethodKey(row.payment_method);
                                                return key ? t(key) : null;
                                            })(),
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                    {/* Lo storico incassi e' il punto in cui si guarda cosa
                                        e' entrato: e' li' che deve saltare all'occhio cosa
                                        non ci e' entrato. */}
                                    <PlatformFeeDueBadge amount={row.platform_fee_due} />
                                </div>

                                <div className="flex flex-col items-end flex-shrink-0 leading-tight">
                                    <span className="font-semibold text-green-700 tabular-nums">
                                        {formatPlanAmount(row.amount)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {formatPlanDate(row.paid_at)}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
};
